import type { AxiosRequestConfig, ResponseType } from 'axios';
import axios from 'axios';
import type { EditableUnit, Piece } from '@/data';

import type { PieceFormat } from '@/formats/PieceParser';
import PieceParser from '@/formats/PieceParser';
import UnitParser from '@/formats/UnitParser';
import { loadAnalysesList, loadAnalysis, uploadAnalysis } from './utils/analyses';
import type { HttpResponse, Response } from './ClientResponse';
import {
  ClientResponse, ClientStatus,
} from './ClientResponse';
import { loadCorpus, loadShowcaseCorpus } from './utils/corpus';
import type { IHttpClient } from './IHttpClient';
import type { User } from './User';
import { login, changePassword } from './User';
import { loadCorpusData } from './utils/corpusData';
import type { PieceSourcesTracks } from './utils/sources';
import { loadPieceSources, loadPieceSourcesLegacy } from './utils/sources';

export class HttpClient implements IHttpClient {
  user: User | null = null;

  private get config(): AxiosRequestConfig {
    const headers: Record<string, string> = {};
    if (this.user) headers.authorization = `Bearer ${this.user.token}`;
    return {
      headers,
      timeout: 20 * 1000,
    };
  }

  baseUrl: string;

  staticRoute: string;

  authUrl: string | null;

  changePwdUrl: string | null;

  isLocalClient: boolean;

  isIllegalUrlComponent(str: string): boolean {
    return !!str.match(/[^A-z0-9-_/.: ]/);
  }

  sourceUrl(url: string, pieceId?: string): Response<string> {
    if (this.isAbsoluteUrl(url)) {
      return this.buildUrl(url);
    }
    return this.buildUrl(`${this.staticRoute}/${pieceId}`, url);
  }

  forgePieceUrl(path: string): string {
    let pieceUrl;
    if (this.isLocalClient) {
      pieceUrl = this.buildUrl(path, '/info.json').data;
    } else if (this.isAbsoluteUrl(path)) {
      pieceUrl = this.buildUrl(path).data;
    } else {
      pieceUrl = `${this.baseUrl}corpus/${path}?static=true`;
    }
    return pieceUrl;
  }

  analysisUrl(piecePath : string, analysisId : string) : Response<string> {
    let analysisResponse : Response<string>;
    if (this.isAbsoluteUrl(analysisId)) {
      analysisResponse = this.buildUrl(analysisId);
    } else if (this.isLocalClient) {
      analysisResponse = this.buildUrl(piecePath, 'analyses', analysisId);
    } else {
      analysisResponse = this.buildUrl('corpus', piecePath, 'analyses', analysisId);
    }

    return analysisResponse;
  }

  isAbsoluteUrl(url : string) : boolean {
    // from https://www.geeksforgeeks.org/javascript/javascript-check-whether-a-url-string-is-absolute-or-relative/
    const RgExp = /^(?:[a-z]+:)?\/\//i;
    return RgExp.test(url);
  }

  /*
    gitlab.com has a strict CORS policy so files must be requested through the public api
    Changes url from something like
    https://gitlab.com/algomus.fr/dezrann/dezrann-corpus/-/raw/main/metadata/test/fugue-bwv862/score-positions.json
    to
    https://gitlab.com/api/v4/projects/algomus.fr%2Fdezrann%2Fdezrann-corpus/repository/files/metadata%2Ftest%2Ffugue-bwv862%2Fscore-positions.json/raw
    doc : https://docs.gitlab.com/api/repository_files/
    */
  formatUrlForGitlabApi(url : string) : string {
    if (url.includes('gitlab.com') && !url.includes('/api/')) {
      const raw = url.split('/raw/')[1].split('/');
      const branch = raw[0];
      const filePath = raw.slice(1).join('/').replaceAll('/', '%2F');
      const projectId = url.split('://gitlab.com/')[1].split('/-/')[0].replaceAll('/', '%2F');
      const apiUrl = `https://gitlab.com/api/v4/projects/${projectId}/repository/files/${filePath}/raw?ref=${branch}`;
      return apiUrl;
    }
    return url;
  }

  /**
   * Builds a URL from a list of string  and returns a typed Response (isOk, isErr…).
   *
   * The function validates the components (illegal characters, empty segments)
   * before constructing the final URL.
   *
   * If the constructed URL is absolute, it is formatted for the GitLab API
   *
   * Otherwise, it is prefixed with this.baseUrl.
   */
  buildUrl(...components: string[]): Response<string> {
    // Check if any illegal characters were passed to the url
    const illegalIndex = components.findIndex(this.isIllegalUrlComponent);
    if (illegalIndex >= 0) {
      return new ClientResponse(
        ClientStatus.Missing,
        new Error(`Illegal character in url: '${components[illegalIndex]}'`),
      ).cast();
    }
    // Check if any url components are empty
    const emptyIndex = components.findIndex((c) => !c);
    if (emptyIndex !== -1 && emptyIndex !== components.length - 1) {
      return ClientResponse.fromParsingError(new Error('Empty url component found in url, and not at the end'));
    }
    if (this.isAbsoluteUrl(components.join('/'))) {
      let url = components.join('/');
      url = this.formatUrlForGitlabApi(url);
      return ClientResponse.fromData(url);
    }

    return ClientResponse.fromData(this.baseUrl + components.join('/'));
  }

  async get<T>(url: string, responseType:ResponseType = 'json'): Promise<HttpResponse<T>> {
    try {
      const typedConfig = this.config;
      typedConfig.responseType = responseType;
      const response = await axios.get<T>(url, typedConfig);
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  folderFormatToArray(folder: unknown): Response<string[]> {
  // Check if string array
    function isStringFormat(format: unknown[]): format is string[] {
      return format.every((f) => typeof f === 'string');
    }

    // Check if file object array
    function isObjectFormat(format: unknown[]): format is {name: string}[] {
      return format.every((f) => f
      && typeof (f as Record<string, string>).name === 'string');
    }

    if (Array.isArray(folder)) {
      if (isObjectFormat(folder)) return ClientResponse.fromData(folder.map((f) => f.name));
      if (isStringFormat(folder)) return ClientResponse.fromData(folder);
    }
    return ClientResponse.fromParsingError(new Error('Invalid Folder Format'));
  }

  async getFolder(url: string): Promise<Response<string[]>> {
    try {
      const response = await axios.get<unknown>(url, this.config);
      const { data } = response;
      return this.folderFormatToArray(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<string[]>(e);
      if (ClientResponse.isClientError(e)) return e as HttpResponse<string[]>;
      throw e;
    }
  }

  async post<T>(
    url: string,
    postData: unknown,
    config:AxiosRequestConfig | null = null,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await axios.post<T>(url, postData, { ...this.config, ...config });
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  async put<T>(url: string, postData: unknown): Promise<HttpResponse<T>> {
    try {
      const response = await axios.put<T>(url, postData, this.config);
      const { data } = response;
      return ClientResponse.fromData(data);
    } catch (e) {
      if (axios.isAxiosError(e)) return ClientResponse.fromAxios<T>(e);
      throw e;
    }
  }

  tryParse<T>(fn: () => T): Response<T> {
    try {
      return ClientResponse.fromData(fn());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed Parsing:', error);
      if (ClientResponse.isClientError<T>(error)) return error;
      return ClientResponse.fromParsingError<T>(error as Error);
    }
  }

  get canLogin(): boolean {
    return !!this.login;
  }

  get canUpload(): boolean {
    return !this.isLocalClient;
  }

  async loadPiece(
    path: string,
    shouldLoadSources = true,
  ): Promise<Response<Piece>> {
    try {
      const pieceUrl = this.forgePieceUrl(path);
      const pieceFormat = (await this.get<PieceFormat>(pieceUrl)).data;
      const isNewFormat = !('images' in pieceFormat.sources || 'audios' in pieceFormat.sources);

      if (pieceFormat.opus && pieceFormat.opus['measure-map']) {
        try {
          if (typeof pieceFormat.opus['measure-map'] === 'string') {
            if (pieceFormat.opus['measure-map'].startsWith('/local-corpus')) {
              const url = this.buildUrl(pieceFormat.opus['measure-map']
                .replace('/local-corpus', '')).data;
              pieceFormat.opus['measure-map'] = (await this.get<string>(url)).data;
            } else if (pieceFormat.opus['measure-map'].startsWith('http')) {
              const url = this.sourceUrl(pieceFormat.opus['measure-map']).data;
              pieceFormat.opus['measure-map'] = (await this.get<string>(url)).data;
            } else {
              const url = `${this.baseUrl}${this.staticRoute}/${path}/${pieceFormat.opus['measure-map']}`;
              pieceFormat.opus['measure-map'] = (await this.get<string>(url)).data;
            }
          }
        } catch (error) {
          pieceFormat.opus['measure-map'] = '';
        }
      }

      let sourcesAndTracks :PieceSourcesTracks = {} as PieceSourcesTracks;
      if (shouldLoadSources) {
        if (isNewFormat) {
          sourcesAndTracks = await loadPieceSources.bind(this)(pieceFormat, path);
        } else {
          sourcesAndTracks = await loadPieceSourcesLegacy.bind(this)(pieceFormat, path);
        }
      }

      return this.tryParse(
        () => PieceParser.fromFormat(
          path,
          this.isLocalClient,
          pieceFormat,
          sourcesAndTracks.sources,
          sourcesAndTracks.tracks,
        ),
      );
    } catch (e) {
      if (ClientResponse.isClientError(e)) return e.cast();
      return ClientResponse.fromParsingError(e as Error);
    }
  }

  async uploadSync(
    path: string,
    synchro: EditableUnit,
  ): Promise<Response<unknown>> {
    try {
      const url = `${this.baseUrl}corpus/${path}/synchro`;
      const obj = UnitParser.exportAudioFormat(synchro);
      const txt = JSON.stringify(obj, null, 2);
      const blob = new Blob([txt], { type: 'application/json' });
      const data = new FormData();
      data.append('synchro', blob);
      const response = (await this.put(url, data)).data;
      return this.tryParse(() => response);
    } catch (e) {
      if (ClientResponse.isClientError(e)) return e.cast();
      return ClientResponse.fromParsingError(e as Error);
    }
  }

  loadCorpus = loadCorpus.bind(this);

  loadCorpusData = loadCorpusData.bind(this);

  loadShowcaseCorpus = loadShowcaseCorpus.bind(this);

  loadAnalysesList = loadAnalysesList.bind(this);

  loadAnalysis = loadAnalysis.bind(this);

  uploadAnalysis = uploadAnalysis.bind(this);

  login = login.bind(this);

  changePassword = changePassword.bind(this);

  constructor(
    baseUrl = 'https://ws.dezrann.net/',
    authUrl: string | null = null,
    changePwdUrl: string | null = null,
    isLocalClient = false,
    staticRoute = '',
    user: User | null = null,
  ) {
    this.baseUrl = baseUrl;
    this.authUrl = authUrl;
    this.changePwdUrl = changePwdUrl;
    this.isLocalClient = isLocalClient;
    this.staticRoute = staticRoute;
    this.user = user;
  }
}
